import { useOrderDispatchRowPinning } from '@components/OrderDispatch/OrderDispatch'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { getCurrentMarket } from '@utils/general'
import { message, notification } from 'antd'
import moment from 'moment/moment'

import type { ResetOrderPayload } from '../useDriverSchedules/requests'

import type {
  AcceptedOrdersRequest,
  CancelOrderRequest,
  CompartmentsPayload,
  DSUOverviewRequest,
  ExistingSupplyPayload,
  Filter,
  ManualCreatePayload,
  ReorderDropsRequest,
  SaveBackhaulRequest,
  SupplyOptionsRequest,
  SwapTrailerRequest,
  TrimPayload,
} from './requests'

import { useSetup } from '@api/useSetup'
import type {
  AcceptedOrdersResponse,
  DSUOverviewResponse,
  DSUPayloadResponse,
  GetOrdersResponse,
  GetReasonCodeResponse,
  OrderByIdResponse,
  getBackhaulMetadataResponse,
} from './responses'
import { validateSupplyOptionsPayload } from './util'

export const useOrders = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()
  const { rowPinningApiRef } = useOrderDispatchRowPinning()

  const setup = useSetup()
  const order_polling_enabled = setup.data?.order_polling_enabled || false
  const order_polling_interval = setup.data?.order_polling_interval || 60

  // Pinned rows exist parallel to the row data, so we need to copy over edits to them as well. These edits are done directly to the objects in memory so we don't reflash the pinned rows
  function updatePinnedRow(modifiedOrCreatedOrder: Gravitate.Order) {
    if (rowPinningApiRef?.current) {
      const topPinnedIndex = rowPinningApiRef.current.pinnedRowData.top.findIndex(
        (row) => row.number === modifiedOrCreatedOrder.number
      )

      if (topPinnedIndex !== -1) {
        const pin = rowPinningApiRef.current.pinnedRowData.top[topPinnedIndex]
        Object.keys(pin).forEach((key) => {
          pin[key] = modifiedOrCreatedOrder[key]
        })

        return
      }
    }
  }

  const useOrdersQuery = (filter?: Filter, isCompact?: boolean) =>
    useQuery({
      queryKey: ['getOrdersQuery', { filter, isCompact }],
      queryFn: ({ queryKey }): GetOrdersResponse => grabOrders(api, queryKey[1]),
      enabled: !!filter,
      refetchOnWindowFocus: false,
      refetchInterval: order_polling_enabled && order_polling_interval * 1000,
    })

  const useAcceptedOrdersQuery = (payload?: AcceptedOrdersRequest) =>
    useQuery({
      queryKey: ['getAcceptedOrdersQuery', payload],
      queryFn: ({ queryKey }): AcceptedOrdersResponse => api.post('order/accepted', queryKey[1]),
      enabled: !!payload && !!payload?.sectors,
      refetchInterval: order_polling_enabled && order_polling_interval * 1000,
    })

  const useOrderReasonCodeQuery = () =>
    useQuery({
      queryKey: ['getOrderReasonCodeQuery'],
      queryFn: (): GetReasonCodeResponse => api.post('order/reason_code/list'),
    })

  const useOrderByIdQuery = (orderId?: string) =>
    useQuery({
      queryKey: ['getOrderByIdQuery', orderId],
      queryFn: (): OrderByIdResponse => api.post('order/by_id', { order_id: orderId }),
      enabled: !!orderId,
    })

  const useBackhaulMetadataQuery = () =>
    useQuery({
      queryKey: ['getBackhaulMetadataQuery'],
      queryFn: (): getBackhaulMetadataResponse => api.post('order/backhaul_metadata'),
    })

  const useAuditHistoryQuery = (orderId: string) =>
    useQuery({
      queryKey: ['getAuditHistoryQuery', orderId],
      queryFn: ({ queryKey }) => api.post('order_audit/history', { order_id: queryKey[1] }),
      enabled: !!orderId,
    })

  const useDSUOverviewQuery = (payload?: DSUOverviewRequest) =>
    useQuery({
      queryKey: ['getDSUOverviewQuery', payload],
      queryFn: ({ queryKey }): DSUOverviewResponse =>
        api.post('device_status_update/overview', { ...queryKey[1], market: getCurrentMarket() }),
      enabled: !Object.values(payload).every((v) => !v),
      refetchInterval: 30000,
    })

  const useDSUPayloadQuery = (id?: string) =>
    useQuery({
      queryKey: ['getDSUPayloadQuery', id],
      queryFn: ({ queryKey }): DSUPayloadResponse => api.post('device_status_update/by_id', { id: queryKey[1] }),
      enabled: !!id,
    })

  const useDefaultCompartmentConfig = (market: string, first_store_number: string) => {
    return useQuery({
      queryKey: ['getDefaultCompartmentConfigQuery', { market, first_store_number }],
      queryFn: ({ queryKey }) => {
        return api.post('trailer/compartments/general', queryKey[1])
      },
      enabled: !!market && !!first_store_number,
    })
  }
  const useSupplyOptionChoicesQuery = (payload?: SupplyOptionsRequest) =>
    useQuery({
      queryKey: ['getSupplyOptionChoicesQuery', payload],
      queryFn: () => {
        // In a by-product order entry flow, users can delete compartments in the middle leaving gaps in the compartment indexes. Backend assumes no gaps, so we're re-indexing the compartments here before we send them off.
        const cloned = structuredClone(payload)
        cloned.pockets = cloned.pockets.map((pocket, compartment_index) => ({ ...pocket, compartment_index }))
        return api.post('order/supply_options', cloned)
      },
      enabled: !!payload && validateSupplyOptionsPayload(payload),
      refetchOnWindowFocus: false,
    })

  const useAuditReportQuery = (payload, market) =>
    useQuery({
      queryKey: ['orderAuditReport', payload, market],
      queryFn: () => api.post('order_audit/report/overview', { ...payload, market }),
      enabled: !!payload.start_date && !!payload.end_date,
    })

  type DeliveryRequestManualAcceptPayload = {
    request_id: string
  }

  const useDeliveryRequestAcceptManualQuery = (payload: DeliveryRequestManualAcceptPayload) =>
    useQuery({
      queryKey: ['deliveryRequestAcceptManualQuery', payload],
      queryFn: () => api.post('delivery_request/manual_supply', null, { queryParams: new URLSearchParams(payload) }),
      enabled: !!payload.request_id,
    })

  const autoAcceptOrderRequestMutation = useMutation({
    mutationFn: (request_id: string) =>
      api.post('delivery_request/auto_supply', null, { queryParams: new URLSearchParams({ request_id }) }),
    onSuccess: (order: Gravitate.Order, requestId) => {
      message.success('Order Request Accepted')
      updatePinnedRow(order)
      queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (cache) => {
        if (!cache) return
        return {
          ...cache,
          rows: cache.rows
            .map((row) =>
              row.id === requestId
                ? {
                    ...order,
                    is_new_order: true,
                    order_request_info: order.order_request_info || row.order_request_info,
                  }
                : row
            )
            .filter((row) => row.state !== 'assigned'),
        }
      })
    },
  })

  const cancelOrderRequestMutation = useMutation({
    mutationFn: (request_id: string, note = '') => api.post('delivery_request/cancel', { request_id, note }),
    onSuccess: (order: Gravitate.Order, requestId) => {
      message.success('Order Request Canceled')
      queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (cache) => {
        if (!cache) return
        return {
          ...cache,
          rows: cache.rows.filter((row) => row.number !== order.number),
        }
      })
    },
  })

  const useBackhaulTemplateQuery = () =>
    useQuery({ queryKey: ['backhaulTemplateQuery'], queryFn: () => api.postBlob('order/backhauls_export') })

  const saveBackhaulMutation = useMutation({
    mutationKey: ['saveBackhaulMutation'],
    mutationFn: (request: SaveBackhaulRequest) => api.post('order/create_backhaul', request),
  })

  const fixUpLoadedSupplyMutation = useMutation({
    mutationKey: ['fixUpLoadedSupplyMutation'],
    mutationFn: (payload: ExistingSupplyPayload) => api.post('order/existing_supply_options', payload),
  })

  const createManualOrderMutation = useMutation({
    mutationKey: ['createManualOrderMutation'],
    mutationFn: ({ payload, resend, send_to_supplier }: ManualCreatePayload) =>
      api.post(
        `order/create_manual`,
        { ...payload },
        { queryParams: new URLSearchParams({ resend, send_to_supplier }) }
      ),
    onSuccess: async (modifiedOrCreatedOrder: any, { payload }) => {
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['orderMovementsQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['siteViewOrders'],
      })

      queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (cache: any[]) => {
        // The driver_note and suppliers columns were never intended to be viewed on the order dispatch grid --
        // After converting the page to AgGrid, we decided to support these and a few other columns from OM.
        // Some of these columns have different schemas on the json response (like driver note) or different keys (suppliers).
        // We're just transforming the create_manual response here to support these columns.
        modifiedOrCreatedOrder.driver_note = modifiedOrCreatedOrder.note.content
        modifiedOrCreatedOrder.suppliers = modifiedOrCreatedOrder.supply_option?.prices?.flatMap((price) =>
          price.components.map((component) => component.supplier)
        )

        if (cache.rows.find((order) => order.id === modifiedOrCreatedOrder.id)) {
          // Weird react-query / JS memory behavior here -- we need to update the cache in place instead of returning a new object
          // to get the cache to update properly. Don't ask why
          cache.rows.forEach((row) => {
            if (row.id === modifiedOrCreatedOrder.id) {
              modifiedOrCreatedOrder.sector = row.sector
              modifiedOrCreatedOrder.load_numbers = row.load_numbers
              modifiedOrCreatedOrder.customer = row.customer
              modifiedOrCreatedOrder.contracts = row.contracts
              row = modifiedOrCreatedOrder
            }
          })
        } else {
          // Because order requests are already in the grid, we need to swap the old row
          // with the new order when the it's manually supplied (order modal)
          if (payload.order_request_id) {
            return {
              ...cache,
              rows: cache.rows
                .map((row) => {
                  if (row.id === payload.order_request_id) {
                    return modifiedOrCreatedOrder
                  }
                  return row
                })
                .filter((row) => row.state !== 'assigned'),
            }
          }

          // We need to check for an assigned driver so that we don't add the order back to the open orders
          // list if it was edited from the the trip grid
          if (!modifiedOrCreatedOrder.assigned_driver && !modifiedOrCreatedOrder.manager) {
            return { ...cache, rows: [modifiedOrCreatedOrder, ...cache.rows] }
          }
        }
      })

      updatePinnedRow(modifiedOrCreatedOrder)
    },
  })

  const cancelOrderMutation = useMutation<{ removed?: number }, any, CancelOrderRequest>({
    mutationKey: ['cancelOrderMutation'],
    mutationFn: (payload) => api.post('order/cancel_order', payload),
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['orderMovementsQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['siteViewOrders'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['getAcceptedOrdersQuery'],
      })
    },
  })

  const trimCompartmentMutation = useMutation({
    mutationKey: ['trimCompartmentMutation'],
    mutationFn: (payload: TrimPayload) => api.post('trailer/compartments/trim', payload),
  })

  const trailerSwapMutation = useMutation({
    mutationKey: ['trailerSwapMutation'],
    mutationFn: (audit_ids: string[]) => api.post('order_audit/trailer_swap_change_summary', { audit_ids }),
  })

  const swapTrailerMutation = useMutation({
    mutationKey: ['swapTrailerMutation'],
    mutationFn: (payload: SwapTrailerRequest) => api.post('order/swap_trailer', payload),
  })

  const reattachCompartmentsMutation = useMutation({
    mutationKey: ['reattachCompartmentsMutation'],
    mutationFn: (payload: CompartmentsPayload) => api.post('order/compartments', payload),
  })

  const reorderDropsMutation = useMutation({
    mutationKey: ['reorderDropsMutation'],
    mutationFn: (payload: ReorderDropsRequest) => api.post('order/update_stop_order', payload),
  })

  const uploadBackhaulMutation = useMutation({
    mutationKey: ['uploadBackhaulMutation'],
    mutationFn: (file) =>
      api.uploadFile('order/backhauls_import', file, {
        headers: { Accept: 'application/json' },
      }),
  })

  const sendCarrierOrderMutation = useMutation<
    any,
    any,
    {
      order_ids: string[]
      force: boolean
      instant?: boolean
      schedule_id: string
      orderStatus: string
      send_email: boolean
    }
  >({
    mutationKey: ['sendCarrierOrder'],
    mutationFn: (request) =>
      api.post('order/send_to_carrier', {
        order_ids: request.order_ids,
        force: request.force,
        instant: request.instant ?? false,
        send_email: request.send_email ?? true,
      }),
    onSuccess: async (newOrders, request) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      message.success('Emails sent')
      await queryClient.cancelQueries({
        queryKey: ['getOrdersQuery'],
      })
      const previousQueries = queryClient.getQueriesData({
        queryKey: ['getOrdersQuery'],
      })

      if (request.orderStatus !== 'assigned_od' || request.force) {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((order) => {
              if (request.order_ids.includes(order.id)) {
                const newOrder = newOrders.find((newOrder) => newOrder.id === order.id)
                if (newOrder) {
                  newOrder.updated_on = moment().toISOString()
                }
                return newOrder || order
              }
              return order
            }),
          }
        })
      } else {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.filter((order) => !request.order_ids.includes(order.id)),
          }
        })
      }

      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (cache: any[]) => {
        if (!cache) return []
        const cacheCopy = structuredClone(cache)

        for (const cacheSchedule of cacheCopy.filter((schedule) => schedule.id === request.schedule_id)) {
          for (const trip of cacheSchedule.trips) {
            if (request.order_ids.includes(trip.order_id)) {
              trip.carrier_notify_state = 'sent'
            }
          }
        }

        return cacheCopy
      })

      // Optimistically update to the new value

      return { previousQueries, request }
    },
  })
  const uncompleteOrderMutation = useMutation({
    mutationKey: ['uncompleteOrderMutation'],
    mutationFn: (request: { order_id: string; orderStatus: string }) =>
      api.post('order/uncomplete', { order_id: request.order_id }),
    onMutate: async (request) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['getOrdersQuery'],
      })

      // Snapshot the previous value

      const previousQueries = queryClient.getQueriesData({
        queryKey: ['getOrdersQuery'],
      })

      if (request.orderStatus !== 'completed_od') {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((order) => {
              if (request.order_id === order.id) {
                const newOrder = {
                  ...order,
                  state: 'sent',
                  carrier_updated: moment().toISOString(),
                  updated_on: moment().toISOString(),
                }
                return newOrder
              }
              return order
            }),
          }
        })
      } else {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.filter((order) => request.order_id !== order.id),
          }
        })
      }
      return { previousQueries, request }
    },
  })

  const completeOrderMutation = useMutation({
    mutationKey: ['completeOrderMutation'],
    mutationFn: (request) =>
      api.post('order/mark_complete', { order_ids: request.order_ids, actual_end_date: request.actual_end_date }),
    onMutate: async (request) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['getOrdersQuery'],
      })

      // Snapshot the previous value

      const previousQueries = queryClient.getQueriesData({
        queryKey: ['getOrdersQuery'],
      })
      if (request.orderStatus === 'all_od') {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((order) => {
              if (request.order_ids.includes(order.id)) {
                const newOrder = {
                  ...order,
                  state: 'complete',
                  carrier_updated: moment(),
                  updated_on: moment().toISOString(),
                }
                return newOrder
              }
              return order
            }),
          }
        })
      } else {
        queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.filter((order) => !request.order_ids.includes(order.id)),
          }
        })
      }
      return { previousQueries, request }
    },
  })
  const markAsDeliveredMutation = useMutation({
    mutationKey: ['markAsDelivered'],
    mutationFn: (request) =>
      api.post('order/mark_complete', { order_ids: request.order_ids, actual_end_date: request.actual_end_date }),
  })

  const resetOrderMutation = useMutation({
    mutationKey: ['resetOrderMutation'],
    mutationFn: (payload: ResetOrderPayload) => api.post('order/reset_order', payload),
    onSuccess: async (newOrders, request) => {
      queryClient.invalidateQueries({
        queryKey: ['orderMovementsQuery'],
      })
      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (cache) => {
        const resetOrder = newOrders[0]
        if (!cache || !resetOrder) return
        return cache.map((order) => (order.id === resetOrder.id ? resetOrder : order))
      })
    },
  })

  const updatePOMutation = useMutation({
    mutationKey: ['updatePOMutation'],
    mutationFn: (payload: { order_id: string; po: string | null }) => api.post('order/update_po', payload),
    onSuccess: async (newOrder, request) => {
      queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
        return {
          ...oldData,
          rows: oldData.rows.map((order) => {
            if (newOrder.id === order.id) {
              return { ...order, po: newOrder.po }
            }
            return order
          }),
        }
      })

      queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (cache) => {
        if (!cache) return
        cache.rows.forEach((order) => {
          if (order.id === newOrder.id) {
            order.po = newOrder.po
          }
        })
        return cache
      })

      updatePinnedRow(newOrder)
    },
  })

  const getCarrierOptionsQuery = (order_id?: string) =>
    useQuery({
      queryKey: ['getSupplyOptionChoicesQuery', order_id],
      queryFn: () =>
        api.post('order/carrier_options', {}, { queryParams: new URLSearchParams({ order_id: order_id! }) }),
      enabled: !!order_id,
      refetchOnWindowFocus: false,
    })

  const stageCarrierOrderMutation = useMutation({
    mutationKey: ['stageCarrierOrderMutation'],
    mutationFn: (order_ids: string[]) => api.post('order/stage_carrier_order', order_ids),
    onSuccess: async (_, request) => {
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
    },
  })
  const unstageCarrierOrderMutation = useMutation({
    mutationKey: ['unstageCarrierOrderMutation'],
    mutationFn: (order_id: string) =>
      api.post('order/unstage_carrier_order', {}, { queryParams: new URLSearchParams({ order_id }) }),
    onSuccess: async (newOrder, request) => {
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
    },
  })

  type ToByCompartmentPayload = {
    trailer_config: string
    compartments?: Gravitate.SupplyOptionCompartmentTarget[]
    fill_trailer?: boolean
  }

  const toByCompartmentMutation = useMutation({
    mutationKey: ['toByCompartmentMutation'],
    mutationFn: (payload: ToByCompartmentPayload) =>
      api.post('trailer/compartments/to_by_compartment', payload) as Promise<Gravitate.SupplyOptionCompartmentTarget[]>,
  })

  const updateOrdersMutation = useMutation({
    mutationKey: ['updateOrderMutation'],
    mutationFn: (payload: Partial<Gravitate.Order>) => api.post('order/update_orders', [payload]),
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['orderMovementsQuery'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['siteViewOrders'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['tankRundownQuery'],
      })
      notification.success({
        message: 'Order Saved',
        description: `Your order was saved successfully. ${payload?.number ? `(Order No. ${payload.number})` : ''}`,
      })
    },
  })

  return {
    useOrdersQuery,
    useAcceptedOrdersQuery,
    useOrderReasonCodeQuery,
    useOrderByIdQuery,
    useBackhaulMetadataQuery,
    useAuditHistoryQuery,
    useDSUOverviewQuery,
    useDSUPayloadQuery,
    useBackhaulTemplateQuery,
    useDefaultCompartmentConfig,
    useSupplyOptionChoicesQuery,
    getCarrierOptionsQuery,
    useAuditReportQuery,
    useDeliveryRequestAcceptManualQuery,
    // Mutations
    autoAcceptOrderRequestMutation,
    cancelOrderRequestMutation,
    uncompleteOrderMutation, // x on order row in carriers in site management
    saveBackhaulMutation,
    completeOrderMutation,
    fixUpLoadedSupplyMutation,
    createManualOrderMutation,
    cancelOrderMutation,
    trimCompartmentMutation,
    swapTrailerMutation,
    trailerSwapMutation,
    reattachCompartmentsMutation,
    reorderDropsMutation,
    uploadBackhaulMutation,
    sendCarrierOrderMutation,
    markAsDeliveredMutation,
    resetOrderMutation,
    updatePOMutation,
    stageCarrierOrderMutation,
    unstageCarrierOrderMutation,
    toByCompartmentMutation,
    updateOrdersMutation,
  }
}

const grabOrders = async (api, { filter, isCompact }) => {
  const ordersData = await api.post('order/overview', { filter }, { ignoreDefaults: isCompact })

  ordersData.rows = ordersData.rows?.map((r) => ({
    ...r,
    carrier: r.carrier || r.manager,
    carrier_updated: r.carrier_updated ?? r.created_date,
  }))

  return ordersData
}
